"use client";

import React, { useRef, useState, useEffect } from 'react';
import { faAngleLeft, faAngleRight, FontAwesomeIcon } from '@/constants/icons';

interface Props {
  children: React.ReactNode;
}

const SimilarMerchantsSlider = ({ children }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 1);
      setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      updateArrows();
      el.addEventListener('scroll', updateArrows);
      window.addEventListener('resize', updateArrows);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', updateArrows);
      }
      window.removeEventListener('resize', updateArrows);
    };
  }, [children]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="relative group/slider w-full">
      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-5 pt-4 pb-4 px-2 -mx-2 no-scrollbar scroll-smooth relative z-10"
      >
        {children}
      </div>

      {/* Left Arrow */}
      {showLeft && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            scroll('left');
          }}
          className="absolute -left-2 lg:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-indigo-600 hover:text-white text-slate-700 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-20 border border-slate-100"
          aria-label="Previous merchants"
        >
          <FontAwesomeIcon icon={faAngleLeft} className="w-4 h-4" />
        </button>
      )}

      {/* Right Arrow */}
      {showRight && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            scroll('right');
          }}
          className="absolute -right-2 lg:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-indigo-600 hover:text-white text-slate-700 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-20 border border-slate-100"
          aria-label="Next merchants"
        >
          <FontAwesomeIcon icon={faAngleRight} className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default SimilarMerchantsSlider;
